"use client";

import { FormEvent, useRef, useState } from "react";
import { type TutorDomainSurface, getCourseAttribution } from "./courseAttribution";
import { getExactDomainPostHogClient } from "./exactDomainPostHogClient";

type CourseInterestOption = {
  value: string;
  label: string;
};

type CourseInterestFormProps = {
  languageSlug: "french" | "dutch";
  languageName: "French" | "Dutch";
  options: CourseInterestOption[];
  surface?: TutorDomainSurface;
};

type SubmissionStatus = "idle" | "submitting" | "success" | "error";

const COURSE_SLUGS = {
  french: "french_intensive_2026_12",
  dutch: "dutch_intensive_2026_12",
} as const;

function createIdempotencyKey() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `course-interest-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function CourseInterestForm({
  languageSlug,
  languageName,
  options,
  surface = "exact-domain",
}: CourseInterestFormProps) {
  const [status, setStatus] = useState<SubmissionStatus>("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const started = useRef(false);
  const idempotencyKey = useRef<string | null>(null);

  function capture(eventName: string, properties: Record<string, unknown> = {}) {
    getExactDomainPostHogClient()?.capture(eventName, {
      course_language: languageName,
      course_slug: COURSE_SLUGS[languageSlug],
      ...getCourseAttribution(languageSlug, surface),
      ...properties,
    });
  }

  function handleFormStart() {
    if (started.current) return;
    started.current = true;
    capture("course_interest_form_started");
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus("submitting");
    setErrorMessage("");

    const form = event.currentTarget;
    const formData = new FormData(form);
    idempotencyKey.current ||= createIdempotencyKey();

    try {
      const response = await fetch("/api/course-interest", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          idempotencyKey: idempotencyKey.current,
          languageSlug,
          firstName: formData.get("first_name"),
          email: formData.get("email"),
          courseVariant: formData.get("course_variant"),
          question: formData.get("question"),
        }),
      });
      const result = await response.json().catch(() => null);

      if (!response.ok || result?.ok !== true || typeof result.id !== "string") {
        throw new Error(`Course interest submission failed with status ${response.status}`);
      }

      setStatus("success");
      capture("course_interest_registered", {
        course_variant: String(formData.get("course_variant") || "unknown"),
      });
    } catch (error) {
      setStatus("error");
      setErrorMessage("We could not register your interest right now. Please try again.");
      capture("course_interest_submission_failed", {
        failure_type: error instanceof Error ? "request_failed" : "unknown",
      });
    }
  }

  if (status === "success") {
    return (
      <section
        className="course-interest-box"
        id="interest"
        aria-labelledby="course-interest-title"
      >
        <p className="course-kicker">Interest registered</p>
        <h2 id="course-interest-title">Thanks! Your interest is registered.</h2>
        <p>
          This does not reserve a place. You can confirm your place now by continuing to payment.
        </p>
        <a className="course-button course-button-primary" href="#register">
          Reserve your spot
        </a>
      </section>
    );
  }

  return (
    <section className="course-interest-box" id="interest" aria-labelledby="course-interest-title">
      <p className="course-kicker">Course interest</p>
      <h2 id="course-interest-title">Register your interest</h2>
      <p>Choose the course option you’re interested in. You can reserve your spot afterwards.</p>

      <form className="course-interest-form" onFocus={handleFormStart} onSubmit={handleSubmit}>
        <label htmlFor={`${languageSlug}-course-first-name`}>
          First name <span>Optional</span>
        </label>
        <input
          id={`${languageSlug}-course-first-name`}
          name="first_name"
          type="text"
          autoComplete="given-name"
          maxLength={100}
        />

        <label htmlFor={`${languageSlug}-course-email`}>Email</label>
        <input
          id={`${languageSlug}-course-email`}
          name="email"
          type="email"
          inputMode="email"
          autoComplete="email"
          maxLength={254}
          required
        />

        <label htmlFor={`${languageSlug}-course-variant`}>Course preference</label>
        <select
          id={`${languageSlug}-course-variant`}
          name="course_variant"
          required
          defaultValue=""
        >
          <option value="" disabled>
            Select an option
          </option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
          <option value="not_sure">Not sure yet</option>
        </select>

        <label htmlFor={`${languageSlug}-course-question`}>
          Question <span>Optional</span>
        </label>
        <textarea
          id={`${languageSlug}-course-question`}
          name="question"
          rows={4}
          maxLength={1000}
          placeholder="Anything you would like us to know?"
        />

        {status === "error" && (
          <p className="course-interest-error" role="alert">
            {errorMessage}
          </p>
        )}

        <button
          className="course-button course-button-primary"
          type="submit"
          disabled={status === "submitting"}
        >
          {status === "submitting" ? "Registering…" : "Register interest"}
        </button>
        <p className="course-interest-privacy">
          We’ll use your details only to contact you about this course.
        </p>
      </form>
    </section>
  );
}
